/**
 * Hook for building field-level change info from backend-provided AugmentedPatchOp[].
 *
 * Replaces the cross-matter `useEntityChanges` hook which computed diffs client-side.
 * Here the backend already provides `changed_fields` as augmented JSON Patch ops,
 * so we just parse those into the same interface.
 */

import { useMemo } from 'react';

import type {
  AugmentedPatchOp,
  EntityChangeType,
} from '@/services/api/generated/entityChangesService';

export interface ChangedFieldsInfo {
  changedPaths: Map<string, { oldValue: unknown; newValue: unknown }>;
  totalChanges: number;
  isFieldChanged: (path: string) => boolean;
  hasChangesUnder: (prefix: string) => boolean;
  getOldValue: (path: string) => unknown | undefined;
  getNewValue: (path: string) => unknown | undefined;
}

const EMPTY: ChangedFieldsInfo = {
  changedPaths: new Map(),
  totalChanges: 0,
  isFieldChanged: () => false,
  hasChangesUnder: () => false,
  getOldValue: () => undefined,
  getNewValue: () => undefined,
};

/**
 * Convert a JSON Patch path like `/data/benefits/0/limitations`
 * to dot-notation like `benefits.0.limitations`.
 */
function patchPathToKey(path: string): string {
  // Strip leading `/data/` prefix (or just `/data`)
  let cleaned = path;
  if (cleaned.startsWith('/data/')) {
    cleaned = cleaned.slice(6);
  } else if (cleaned.startsWith('/data')) {
    cleaned = cleaned.slice(5);
  } else if (cleaned.startsWith('/')) {
    cleaned = cleaned.slice(1);
  }
  return cleaned.replace(/\//g, '.');
}

export function useChangedFields(
  changeType: EntityChangeType,
  changedFields: AugmentedPatchOp[],
): ChangedFieldsInfo {
  return useMemo(() => {
    if (changeType !== 'updated' || !changedFields || changedFields.length === 0) {
      return EMPTY;
    }

    const changedPaths = new Map<string, { oldValue: unknown; newValue: unknown }>();

    changedFields.forEach((op) => {
      const key = patchPathToKey(op.path);
      if (key) {
        changedPaths.set(key, {
          oldValue: op.old_value,
          newValue: op.new_value,
        });
      }
    });

    const topLevelKeys = new Set(
      Array.from(changedPaths.keys()).map((p) => p.split('.')[0]),
    );

    return {
      changedPaths,
      totalChanges: topLevelKeys.size,
      isFieldChanged: (p: string) => changedPaths.has(p),
      hasChangesUnder: (prefix: string) =>
        Array.from(changedPaths.keys()).some(
          (key) => key === prefix || key.startsWith(`${prefix}.`),
        ),
      getOldValue: (p: string) => changedPaths.get(p)?.oldValue,
      getNewValue: (p: string) => changedPaths.get(p)?.newValue,
    };
  }, [changeType, changedFields]);
}
